import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
  noIndex?: boolean;
  geoRegion?: string;
}

export default function SEO({ 
  title, 
  description, 
  keywords, 
  canonicalUrl,
  ogType = 'website',
  ogImage,
  structuredData,
  noIndex = false,
  geoRegion
}: SEOProps) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta description
    if (description) {
      updateMetaTag('description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical URL
    if (canonicalUrl) {
      updateCanonicalUrl(canonicalUrl);
    }

    // Update Open Graph tags
    if (title) {
      updateMetaProperty('og:title', title);
    }
    if (description) {
      updateMetaProperty('og:description', description);
    }
    if (ogType) {
      updateMetaProperty('og:type', ogType);
    }
    if (canonicalUrl) {
      updateMetaProperty('og:url', canonicalUrl);
    }
    if (ogImage) {
      updateMetaProperty('og:image', ogImage);
    }

    // Update Twitter tags
    if (title) {
      updateMetaProperty('twitter:title', title);
    }
    if (description) {
      updateMetaProperty('twitter:description', description);
    }
    if (ogImage) {
      updateMetaProperty('twitter:image', ogImage);
    }

    // Ensure robots meta tag
    updateRobotsTag(noIndex);

    // Update geo.region meta tag
    if (geoRegion) {
      updateMetaTag('geo.region', geoRegion);
    }

    // Add structured data
    if (structuredData) {
      addStructuredData(structuredData);
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, structuredData, noIndex, geoRegion]);

  const updateMetaTag = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const updateMetaProperty = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const updateRobotsTag = (shouldNoIndex: boolean) => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');
  };

  const updateCanonicalUrl = (url: string) => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  };

  const addStructuredData = (data: object) => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"][data-dynamic="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  };

  return null; // This component doesn't render anything
}

// SEO constants for buyer-focused keywords - 2026 yacht-first
export const BUYER_KEYWORDS = [
  // Yacht buyer keywords (primary)
  'fair price for a yacht',
  'yacht buying guide',
  'what should I pay for a yacht',
  'what should I pay for a motor yacht',
  'luxury yacht fair market value',
  'yacht purchase price guide',
  'yacht price estimator',
  'yacht price checker',
  'first time yacht buyer',
  'yacht buyer tips',
  'buying a yacht checklist',
  'how to negotiate yacht price',
  'avoid overpaying for a yacht',
  'good deal on a yacht',
  // Dual-keyword boat terms (SEO preservation)
  'fair boat price',
  'fair price for a boat',
  'fair market value boat',
  'what should I pay for a boat',
  'how much should I pay for a boat',
  'boat buying price guide',
  'boat purchase price estimator',
  'boat price negotiation',
  'boat buying guide',
  'used boat buying guide',
  'boat price checker',
  'boat price estimator'
].join(', ');

// SEO constants for yacht/boat valuation keywords - 2026 yacht-first with dual-keyword strategy
export const BOAT_VALUATION_KEYWORDS = [
  // Yacht-first core keywords
  'yacht values',
  'yacht value',
  'yacht valuation',
  'yacht appraisal',
  'yacht market value',
  'yacht pricing guide',
  'yacht value calculator',
  'yacht value estimator',
  'yacht trade-in value',
  'yacht market analysis',
  'yacht investment value',
  'yacht depreciation',
  'luxury yacht values',
  'motor yacht values',
  'sailboat values',
  'catamaran values',
  'marine valuation',
  'marine appraisal',
  // Yacht question keywords
  'what is my yacht worth',
  'how much is my yacht worth',
  'value my yacht',
  'sell my yacht',
  'yacht resale value',
  // Dual-keyword boat terms (SEO traffic preservation)
  'boat values',
  'boat value',
  'boat valuation',
  'boat appraisal',
  'boat worth',
  'boat value estimator',
  'boat value calculator',
  'used boat values',
  'boat value lookup',
  'boat value by vin',
  'boat value by hin',
  'boat trade-in value',
  'what is my boat worth',
  'how much is my boat worth',
  // Brand comparisons (high traffic)
  'nada boat values',
  'kelley blue book boats',
  'kbb boat values',
  'jd power boat values',
  // Regional
  'yacht values florida',
  'yacht values california',
  'yacht values texas',
  'boat values florida',
  'boat values california',
  // 2026 keywords
  'confidential yacht valuation',
  'professional yacht appraisal',
  'current yacht values',
  'yacht values guide 2026',
  'accurate yacht values'
].join(', ');

export const BOAT_VALUATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "YachtValues™ - Instant Yacht Valuation",
  "description": "Instant yacht valuation and market intelligence platform for luxury vessels, providing data-driven insights for discerning owners, buyers, and advisors.",
  "url": "https://yachtvalues.com",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Any",
  "browserRequirements": "Modern web browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Instant yacht valuation service"
  },
  "provider": {
    "@type": "Organization",
    "name": "YachtValues",
    "url": "https://yachtvalues.com",
    "email": "contact@yachtvalues.com"
  },
  "featureList": [
    "AI-powered yacht valuation",
    "Instant market intelligence",
    "Private market analysis and trends",
    "Professional yacht appraisal",
    "Data-driven insights",
    "Comprehensive market reports"
  ]
};